import { useEffect, useState, useRef, useCallback } from 'react';
import { clsx } from 'clsx';
import { Flex, Text, Button, Icon } from '@nula/core-ui';
import { fetchGifs } from '@/lib/gifs/gif-client';
import type { GifResult, GifMode } from './types';
import { useLaunchAnimation } from '@/components/popover-launch/use-launch-animation';
import { KlipyLogo } from './klipy-logo';
import styles from './gif-picker.module.css';

const PAGE_SIZE = 20;

interface CacheEntry {
  results: GifResult[];
  totalCount: number;
  offset: number;
}

/** Module-level cache — survives re-mounts within the same session. */
export const gifCache = new Map<string, CacheEntry>();
const cache = gifCache;

function cacheKey(mode: GifMode, query: string, limit: number) {
  return `${mode}:${query}:${limit}`;
}

interface GifPickerProps {
  mode: GifMode;
  /** Controlled search query — empty string shows trending */
  query: string;
  onSelect: (result: GifResult) => void;
  /** Max results per page; defaults to 20 */
  limit?: number;
  /** When true, picker fills its flex container height (ToolPanel use) */
  fillContainer?: boolean;
  /** Delay first fetch by this many ms — use to stagger concurrent pickers */
  fetchDelay?: number;
}

type LoadStatus = 'idle' | 'loading' | 'error';

export function GifPicker({ mode, query, onSelect, limit = PAGE_SIZE, fillContainer = false, fetchDelay = 0 }: GifPickerProps) {
  const key = cacheKey(mode, query, limit);
  const cached = cache.get(key);

  const [results, setResults] = useState<GifResult[]>(cached?.results ?? []);
  const [offset, setOffset] = useState(cached?.offset ?? 0);
  const [totalCount, setTotalCount] = useState(cached?.totalCount ?? 0);
  const [status, setStatus] = useState<LoadStatus>(cached ? 'idle' : 'loading');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const resetKeyRef = useRef(key);
  const { launchingId, launch } = useLaunchAnimation();

  const hasMore = results.length < totalCount;
  const isInitialLoad = status === 'loading' && results.length === 0;

  const loadPage = useCallback((pageOffset: number, resetKey: string) => {
    setStatus('loading');
    fetchGifs(mode, query, limit, pageOffset)
      .then(({ results: newResults, totalCount: total }) => {
        if (resetKeyRef.current !== resetKey) return;
        const next = pageOffset === 0 ? newResults : [...(cache.get(resetKey)?.results ?? []), ...newResults];
        const nextOffset = pageOffset + newResults.length;
        cache.set(resetKey, { results: next, totalCount: total, offset: nextOffset });
        setTotalCount(total);
        setResults(next);
        setOffset(nextOffset);
        setStatus('idle');
      })
      .catch(() => {
        if (resetKeyRef.current !== resetKey) return;
        setStatus('error');
      });
  }, [mode, query, limit]);

  // Reset when mode/query/limit changes — serve from cache if available
  useEffect(() => {
    const nextKey = cacheKey(mode, query, limit);
    resetKeyRef.current = nextKey;
    const hit = cache.get(nextKey);
    if (hit) {
      setResults(hit.results);
      setOffset(hit.offset);
      setTotalCount(hit.totalCount);
      setStatus('idle');
      return;
    }
    setResults([]);
    setOffset(0);
    setTotalCount(0);
    setStatus('loading');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = fetchDelay + (query ? 300 : 0);
    debounceRef.current = setTimeout(() => loadPage(0, nextKey), delay);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [mode, query, limit, loadPage, fetchDelay]);

  // IntersectionObserver: when sentinel enters view, load next page.
  // We use a ref for status/hasMore/offset so the callback always sees
  // current values without needing to re-create the observer on every render.
  const scrollStateRef = useRef({ status, hasMore, offset });
  scrollStateRef.current = { status, hasMore, offset };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        const { status: s, hasMore: hm, offset: off } = scrollStateRef.current;
        if (s === 'idle' && hm) {
          loadPage(off, resetKeyRef.current);
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 },
    );

    observer.observe(sentinel);

    // If the sentinel is already visible when observer mounts (e.g. right after
    // a page load completes and the list is short), trigger immediately.
    const rect = sentinel.getBoundingClientRect();
    const root = scrollContainerRef.current;
    const rootRect = root ? root.getBoundingClientRect() : null;
    const alreadyVisible = rootRect
      ? rect.top < rootRect.bottom && rect.bottom > rootRect.top
      : rect.top < window.innerHeight && rect.bottom > 0;
    if (alreadyVisible && status === 'idle' && hasMore) {
      loadPage(offset, resetKeyRef.current);
    }

    return () => observer.disconnect();
  // Re-run only when hasMore flips or loadPage identity changes (mode/query/limit change).
  // status and offset are read from the ref inside the callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadPage]);

  return (
    <Flex vertical className={clsx(styles.picker, fillContainer && styles.fillContainer)}>
      {isInitialLoad && (
        <div className={styles.skeleton} data-testid="gif-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={styles.skeletonCell} />
          ))}
        </div>
      )}

      {status === 'error' && results.length === 0 && (
        <Flex vertical align="center" justify="center" gap="sm" className={styles.error}>
          <Icon name="wifi_off" size={28} />
          <Text type="secondary" size="sm">GIFs unavailable</Text>
          <Button variant="text" size="sm" onClick={() => {
            setStatus('loading');
            loadPage(0, resetKeyRef.current);
          }}>
            Retry
          </Button>
        </Flex>
      )}

      {results.length > 0 && mode === 'gifs' && (
        /* scrollWrapper is the actual scroll container and IntersectionObserver root.
           masonryGrid is a pure columns layout — sentinel lives OUTSIDE it so
           getBoundingClientRect reports the true bottom, not a column position. */
        <div ref={scrollContainerRef} className={styles.scrollWrapper}>
          <div className={styles.masonryGrid}>
            {results.map(result => (
              <button
                key={result.id}
                type="button"
                className={styles.masonryItem}
                aria-label={result.title}
                data-launching={launchingId === result.id || undefined}
                onClick={() => launch(result.id, () => onSelect(result))}
              >
                <img
                  src={result.stillUrl}
                  alt={result.title}
                  className={styles.masonryImg}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
          {hasMore && <div ref={sentinelRef} className={styles.sentinel} />}
          {status === 'loading' && results.length > 0 && (
            <div className={styles.pageLoader} aria-label="Loading more" />
          )}
        </div>
      )}

      {results.length > 0 && mode === 'stickers' && (
        <div ref={scrollContainerRef} className={styles.scrollWrapper}>
          <div className={styles.stickerGrid}>
            {results.map(result => (
              <button
                key={result.id}
                type="button"
                className={styles.stickerItem}
                aria-label={result.title}
                data-launching={launchingId === result.id || undefined}
                onClick={() => launch(result.id, () => onSelect(result))}
              >
                <img
                  src={result.webpUrl ?? result.stillUrl}
                  alt={result.title}
                  className={styles.stickerImg}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
          {hasMore && <div ref={sentinelRef} className={styles.sentinel} />}
          {status === 'loading' && results.length > 0 && (
            <div className={styles.pageLoader} aria-label="Loading more" />
          )}
        </div>
      )}

      {results.length > 0 && (
        <Flex justify="end" align="center" className={styles.attribution}>
          <a
            href="https://klipy.com"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.attributionLink}
            aria-label="Powered by Klipy"
          >
            <span className={styles.attributionPoweredBy}>Powered by</span>
            <KlipyLogo className={styles.attributionLogo} />
          </a>
        </Flex>
      )}
    </Flex>
  );
}
